import { useEffect, useRef, useState } from 'react'
import {
  getAllPlanetData, getPredictionsCached, getSaturnTransitSign,
  computeManglik, computeKalsarpa, sadesatiStatus,
  extractYogas, prettifyPredictionName, remedyFor,
} from '@astro/vedastro'
import { Badge, CardsSkeleton, PLANET_GLYPHS, StudioError } from '../StudioBits'
import { manglikVerdict, kalsarpaVerdict, sadesatiVerdict } from '../doshaText'

function DoshaCard({ icon, title, present, verdict, detail }) {
  return (
    <div className={`ks-card ks-dosha-card${present ? ' present' : ''}`}>
      <div className="ks-card-top">
        <span className="ks-card-icon">{icon}</span>
        <Badge tone={present ? 'red' : 'green'}>{verdict}</Badge>
      </div>
      <h4>{title}</h4>
      <p>{detail}</p>
    </div>
  )
}

/**
 * Yoga & Dosha — classical dosha rules applied to live chart facts, the named
 * yogas found by the predictions engine, and graha-shanti remedies for the
 * planets the chart shows as afflicted.
 */
export default function YogasPart({ birth }) {
  const [status, setStatus] = useState('loading') // loading | error | done
  const [data, setData] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    const args = { dob: birth.dob, tob: birth.tob, location: birth.location }
    const today = new Date().toISOString().slice(0, 10)
    Promise.all([
      getAllPlanetData(args),
      getPredictionsCached(args),
      // Best-effort — without it the Sadesati card reports "unknown".
      getSaturnTransitSign(today).catch(() => null),
    ])
      .then(([planets, predictions, saturnSign]) => {
        if (my !== reqRef.current) return
        const moon = planets.find((p) => p.name === 'Moon')
        setData({
          manglik: computeManglik(planets),
          kalsarpa: computeKalsarpa(planets),
          sadesati: saturnSign && moon ? sadesatiStatus(moon.sign, saturnSign) : null,
          yogas: extractYogas(predictions),
          afflicted: planets.map((p) => ({ ...p, remedy: remedyFor(p.name) })).filter((p) => p.afflicted && p.remedy),
        })
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not run the dosha checks right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') return <CardsSkeleton cards={6} />
  if (status === 'error') return <StudioError text={errorMsg} onRetry={load} />
  if (!data) return null

  const { manglik, kalsarpa, sadesati, yogas, afflicted } = data

  return (
    <div>
      {/* Dosha verdicts */}
      <p className="ks-group-title">Dosha Check</p>
      <div className="ks-card-grid">
        <DoshaCard icon="♂" title="Mangal Dosha (Manglik)" {...manglikVerdict(manglik)} />
        <DoshaCard icon="🐍" title="Kalsarpa Dosha" {...kalsarpaVerdict(kalsarpa)} />
        <DoshaCard icon="♄" title="Shani Sadesati" {...sadesatiVerdict(sadesati)} />
      </div>

      {/* Named yogas */}
      <p className="ks-group-title">Planetary Yogas in Your Chart · {yogas.length}</p>
      {yogas.length ? (
        <div className="ks-card-grid">
          {yogas.map((y) => (
            <div className="ks-card" key={y.name}>
              <div className="ks-card-top">
                <span className="ks-card-icon">✨</span>
                {y.planets?.length > 0 && (
                  <span className="ks-card-planets">
                    {y.planets.map((p) => PLANET_GLYPHS[p] || p).join(' ')}
                  </span>
                )}
              </div>
              <h4>{prettifyPredictionName(y.name)}</h4>
              <p>{y.text}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="ks-note">No named yogas surfaced for this chart.</p>
      )}

      {/* Remedies for afflicted grahas */}
      <p className="ks-group-title">Graha Shanti — Suggested Remedies · {afflicted.length}</p>
      {afflicted.length ? (
        <div className="ks-card-grid">
          {afflicted.map((p) => (
            <div className="ks-card ks-remedy-card" key={p.name}>
              <div className="ks-card-top">
                <span className="ks-card-icon">{p.remedy.glyph}</span>
                <Badge tone="amber">Needs Support</Badge>
              </div>
              <h4>{p.name} · {p.remedy.deity}</h4>
              <div className="ks-remedy-rows">
                <p><strong>Mantra</strong> {p.remedy.mantra} ({p.remedy.japa})</p>
                <p><strong>Gemstone</strong> {p.remedy.gemstone}</p>
                <p><strong>Fasting</strong> {p.remedy.fasting}</p>
                <p><strong>Charity</strong> {p.remedy.charity}</p>
                <p><strong>Practice</strong> {p.remedy.practice}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="ks-note">All seven classical grahas are well placed — no shanti remedies are called for. 🙏</p>
      )}

      <p className="ks-note">
        Dosha rules follow the classical definitions applied to your computed chart. Always confirm remedies —
        especially gemstones — with Maharaj Ji before adopting them.
      </p>
    </div>
  )
}
